import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMapLayerAttributes } from '../../components/useMapLayerAttributes.ts';
import { useWatchedMapLayerSource } from '../useWatchedMapLayerSource.ts';
import { SortableAttributeList } from './SortableAttributeList.tsx';

export interface IMapLayerIntersectionAttributesInputProps {
  prefix: string;
}

const LAYER_PREFIX_REGEX = /^(.*\.)\w+\.$/;

/**
 * Estrae il prefix del layer padre dal prefix della sezione.
 * Es. `layers.0.intersection.` → `layers.0.`
 *     `layers.2.identify.`     → `layers.2.`
 */
function getLayerPrefix(sectionPrefix: string): string {
  const match = sectionPrefix.match(LAYER_PREFIX_REGEX);
  return match?.[1] ?? '';
}

/**
 * Input per attributi intersezione/identify con alias opzionali.
 * Gestisce la sincronizzazione tra attributes[] e aliases{}.
 *
 * Se l'introspezione del layer restituisce attributi disponibili
 * (useMapLayerAttributes), il Select mostra le opzioni dal servizio.
 * Altrimenti resta in modalita' tags (inserimento manuale). mode="tags"
 * e' sempre attivo per permettere attributi non esposti dall'introspezione.
 *
 * La SelectInput gestisce aggiunta/rimozione degli attributi.
 * La SortableAttributeList gestisce l'ordine (drag-and-drop) e gli alias.
 */
export function MapLayerIntersectionAttributesInput(
  props: IMapLayerIntersectionAttributesInputProps,
) {
  const { prefix } = props;
  const { getValues } = useFormContext();
  const setValue = useFormSetValue();

  const layerPrefix = getLayerPrefix(prefix);
  const layerSource = useWatchedMapLayerSource(layerPrefix);

  const attributes: string[] = useWatch({ name: `${prefix}attributes` }) ?? [];

  const { availableAttributes, isLoading } = useMapLayerAttributes(layerSource);

  const attributeOptions = useMemo(() => {
    const availableOptions = availableAttributes.map(attribute => ({
      value: attribute.name,
      label:
        attribute.alias && attribute.alias !== attribute.name
          ? `${attribute.name} (${attribute.alias})`
          : attribute.name,
    }));

    if (availableOptions.length > 0) {
      const availableNames = new Set(
        availableOptions.map(option => option.value),
      );
      const manualOptions = attributes
        .filter(attr => !availableNames.has(attr))
        .map(attr => ({ value: attr, label: attr }));
      return [...availableOptions, ...manualOptions];
    }

    return attributes.map(attribute => ({
      value: attribute,
      label: attribute,
    }));
  }, [attributes, availableAttributes]);

  // Quando l'utente rimuove un attributo, elimina anche l'alias corrispondente.
  useEffect(() => {
    const currentAliases: Record<string, string> =
      getValues(`${prefix}aliases`) ?? {};
    const currentKeys = Object.keys(currentAliases);
    if (currentKeys.length === 0) return;

    const validKeys = new Set(attributes);
    const cleaned = Object.fromEntries(
      currentKeys
        .filter(key => validKeys.has(key))
        .map(key => [key, currentAliases[key]]),
    );

    if (Object.keys(cleaned).length !== currentKeys.length) {
      setValue(`${prefix}aliases`, cleaned);
    }
  }, [attributes, getValues, prefix, setValue]);

  return (
    <>
      <FormItem
        label="Attributi"
        name={`${prefix}attributes`}
        help={
          availableAttributes.length > 0
            ? 'Seleziona tra gli attributi disponibili dal servizio oppure digita manualmente'
            : 'Nomi degli attributi da estrarre dalle feature (inserimento manuale)'
        }
      >
        <SelectInput
          name={`${prefix}attributes`}
          mode="tags"
          loading={isLoading}
          options={attributeOptions}
          placeholder={
            availableAttributes.length > 0
              ? 'Seleziona o digita un attributo...'
              : 'Digita un nome attributo e premi Invio...'
          }
        />
      </FormItem>
      <SortableAttributeList prefix={prefix} attributes={attributes} />
    </>
  );
}
